import React, { RefObject, ReactElement, ReactNode } from "react";
import { Formik, FormikTouched } from "formik";
import { ProfileManageMode } from "src/models/ProfileManageMode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { Button } from "reactstrap";

interface WizardProps {
  initialValues: any;
  mode: ProfileManageMode;
  ref: RefObject<FormikWizard>;
  validationSchemas: any;
  onCancel: () => void;
  onSubmit: (values: any) => Promise<any>;
  onNext: (values: any, page: number) => Promise<any>;
  children: any;
}
interface FormikWizardState {
  page: number;
  values: any;
}
// eslint-disable-next-line import/prefer-default-export
export class FormikWizard extends React.Component<WizardProps, FormikWizardState> {
  public static Page = ({ children, values = {} }: { children: any; name: string; values?: any }) => {
    return children(values);
  };

  private formikRef = React.createRef();

  constructor(props: WizardProps) {
    super(props);
    this.state = {
      page: 0,
      values: props.initialValues,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.cancel = this.cancel.bind(this);
  }

  /* special methods which are called by ref from parent */
  public setFormSubmitting = (isSubmitting: boolean): void => {
    (this.formikRef.current as any).setSubmitting(isSubmitting);
  };

  public getFormValues = (): any => {
    return (this.formikRef.current as any).values;
  };

  /* */
  public render(): ReactNode {
    const { children } = this.props;
    const { page, values } = this.state;

    const activePage = React.Children.toArray(children)[page];
    const isLastPage = page === React.Children.count(children) - 1;
    return (
      <Formik
        innerRef={this.formikRef as any}
        initialValues={values}
        enableReinitialize={true}
        validationSchema={this.props.validationSchemas[this.state.page]}
        onSubmit={this.handleSubmit}
      >
        {({
          values: formVals,
          handleSubmit,
          isSubmitting,
          setSubmitting,
          validateForm,
          setTouched,
          submitForm,
        }): ReactNode => (
          <>
            <div className="mb-4">
              <h4 className="cta fw500">Edit Profile</h4>
            </div>
            <div className="d-none d-sm-inline">
              <div className="d-flex justify-content-center">
                <ul className="progressbar">
                  {React.Children.toArray(children).map((child: any, index: number) => {
                    return (
                      <li
                        key={index}
                        className={`${index < this.state.page ? "done" : ""} ${
                          index === this.state.page ? "focus" : ""
                        }`}
                      >
                        {child.props.name}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="mt-3">
              {React.cloneElement(activePage as ReactElement, { values: formVals })}
              <div className="buttons mt-4 d-flex">
                {page > 0 && (
                  <Button
                    color="primary"
                    className="me-1"
                    onClick={(): void => {
                      this.previous(setSubmitting);
                    }}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                  </Button>
                )}
                {!isLastPage && (
                  <Button
                    color="primary"
                    onClick={(): void => {
                      validateForm()
                        .then((result) => {
                          // eslint-disable-next-line promise/always-return
                          if (Object.keys(result).length === 0) {
                            this.next((this.formikRef.current as any).values, setTouched, setSubmitting);
                          } else {
                            setTouched(result as FormikTouched<any>);
                          }
                        })
                        .catch((error) => {
                          console.error(error);
                        });
                    }}
                    disabled={isSubmitting}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                )}
                <div className="ms-auto">
                  <Button color="primary" className="primary me-1" onClick={this.cancel} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  {isLastPage && (
                    <Button
                      color="secondary"
                      onClick={(): void => {
                        validateForm()
                          .then((result) => {
                            // eslint-disable-next-line promise/always-return
                            if (Object.keys(result).length === 0) {
                              submitForm();
                            } else {
                              setTouched(result as FormikTouched<any>);
                            }
                          })
                          .catch((error) => {
                            console.error(error);
                          });
                      }}
                      disabled={isSubmitting}
                    >
                      Save
                    </Button>
                  )}
                  {!isLastPage && (
                    <Button
                      color="secondary"
                      disabled={isSubmitting}
                      onClick={(): void => {
                        validateForm()
                          .then((result) => {
                            // eslint-disable-next-line promise/always-return
                            if (Object.keys(result).length === 0) {
                              submitForm();
                            } else {
                              setTouched(result as FormikTouched<any>);
                            }
                          })
                          .catch((error) => {
                            console.error(error);
                          });
                      }}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </>
        )}
      </Formik>
    );
  }

  private next = (values: any, setTouched: any, setSubmitting: any): void => {
    setTouched({});
    setSubmitting(true);
    // save the form
    this.props
      .onNext(values, this.state.page)
      .then((result) => {
        // then
        setSubmitting(false);
        if (typeof window !== "undefined") {
          window.scrollTo(0, 0);
        }
        return this.setState((state) => ({
          page: Math.min(state.page + 1, this.props.children.length - 1),
          values: result.profile,
        }));
      })
      .catch((error) => {
        console.error(error);
        setSubmitting(false);
      });
  };

  private previous = (setSubmitting: (val: boolean) => void): void => {
    setSubmitting(true);
    this.setState((state: FormikWizardState) => ({
      page: Math.max(state.page - 1, 0),
    }));
    setSubmitting(false);
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  };

  private handleSubmit(values: any, bag: any): Promise<any> {
    bag.setSubmitting(true);
    return this.props.onSubmit(values).catch(() => {
      bag.setSubmitting(false);
    });
  }

  private cancel = (): void => {
    this.props.onCancel();
  };
}
